import { TeamsInterface, TeamsStatus, TeamRole } from "ts-interfaces";

/**
 * A seat is consumed when a non-admin member is in ACTIVE or INVITED status.
 * Mirrors the count from apps/design-firm Teams.tsx (admins do not consume seats;
 * INACTIVE / INVITED_INACTIVE / ARCHIVED members do not consume seats).
 */
const consumesSeat = (m: Pick<TeamsInterface, "role" | "status">): boolean => {
  if (m.role === TeamRole.ADMIN) return false;
  return m.status === TeamsStatus.ACTIVE || m.status === TeamsStatus.INVITED;
};

export const computeSeatsUsed = (
  members: ReadonlyArray<Pick<TeamsInterface, "role" | "status">>,
): number => members.filter(consumesSeat).length;

export interface SeatSummary {
  totalSeats: number;
  usedSeats: number;
  availableSeats: number;
}

export const computeSeatSummary = (
  members: ReadonlyArray<Pick<TeamsInterface, "role" | "status">>,
  totalSeats: number,
): SeatSummary => {
  const used = computeSeatsUsed(members);
  return {
    totalSeats,
    usedSeats: used,
    availableSeats: Math.max(0, totalSeats - used),
  };
};

/**
 * Can a new member with `role` be added without exceeding the seat cap?
 * Admin invites never consume a seat, so they're always allowed.
 */
export const canAddMemberWithRole = (
  summary: SeatSummary,
  role: TeamRole | `${TeamRole}`,
): boolean => {
  if (role === TeamRole.ADMIN) return true;
  return summary.availableSeats > 0;
};
